import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Camera,
  Upload,
  Video as VideoIcon,
  Music,
  Trash2,
  Search,
  Edit2,
  X,
  Check,
  User as UserIcon,
  ChevronDown,
} from "lucide-react";
import { supabase } from "@/lib/supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAGE_SIZE = 24;

function normalizeVideos(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.videos)) return payload.videos;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.results)) return payload.results;
    if (Array.isArray(payload.items)) return payload.items;
  }
  return [];
}

function bytesToMB(bytes) {
  if (!bytes || Number.isNaN(Number(bytes))) return 0;
  return Number(bytes) / (1024 * 1024);
}

function formatMB(value) {
  const n = Number(value) || 0;
  // 0.0 / 0.00 según tamaño
  if (n < 10) return n.toFixed(2);
  return n.toFixed(0);
}

const Dashboard = () => {
  const nav = useNavigate();

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const [editingVideo, setEditingVideo] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", video_type: "" });

  // Paginación
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Usuario
  const [userLabel, setUserLabel] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [hasDisplayName, setHasDisplayName] = useState(false);

  // ✅ Cuota (barra)
  const [quota, setQuota] = useState({
    quota_bytes: 0,
    used_bytes: 0,
    remaining_bytes: 0,
  });

  const safeVideos = useMemo(() => (Array.isArray(videos) ? videos : []), [videos]);

  // Debounce búsqueda
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const getSessionTokenOrRedirect = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token) {
      nav("/login");
      return null;
    }
    return session.access_token;
  }, [nav]);

  // ✅ Cargar cuota
  const fetchQuota = useCallback(async () => {
    try {
      if (!BACKEND_URL) return;

      const token = await getSessionTokenOrRedirect();
      if (!token) return;

      const res = await axios.get(`${API}/me/quota`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const qb = Number(res.data?.quota_bytes || 0);
      const ub = Number(res.data?.used_bytes || 0);
      const rb =
        Number(res.data?.remaining_bytes) ||
        Math.max(0, qb - ub);

      setQuota({ quota_bytes: qb, used_bytes: ub, remaining_bytes: rb });
    } catch (e) {
      console.error("Error cargando cuota:", e);
    }
  }, [getSessionTokenOrRedirect]);

  // Cargar label usuario
  const loadUserLabel = useCallback(async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const sessionUser = session?.user;
      const sessionName = sessionUser?.user_metadata?.display_name;
      const sessionEmail = sessionUser?.email || "";

      if (sessionEmail) setUserEmail(sessionEmail);

      if (sessionName && String(sessionName).trim()) {
        setUserLabel(String(sessionName).trim());
        setHasDisplayName(true);
        return;
      }

      const { data, error } = await supabase.auth.getUser();
      if (error) throw error;

      const user = data?.user;
      const name = user?.user_metadata?.display_name;
      const email = user?.email || sessionEmail;

      setUserEmail(email || "");

      const cleanName = name && String(name).trim() ? String(name).trim() : "";
      if (cleanName) {
        setUserLabel(cleanName);
        setHasDisplayName(true);
      } else {
        setUserLabel(email || "Usuario");
        setHasDisplayName(false);
      }
    } catch (e) {
      console.error("Error cargando usuario:", e);
    }
  }, []);

  const setOrEditMyName = async () => {
    const nuevoNombre = window.prompt("¿Qué nombre o nickname quieres que aparezca?");
    if (!nuevoNombre) return;
    const clean = nuevoNombre.trim();
    if (!clean) return;

    try {
      const { error } = await supabase.auth.updateUser({ data: { display_name: clean } });
      if (error) {
        alert("No se pudo guardar el nombre: " + error.message);
        return;
      }
      setUserLabel(clean);
      setHasDisplayName(true);
      alert("Nombre guardado ✅");
    } catch (e) {
      console.error("Error updateUser:", e);
      alert("Error inesperado guardando el nombre.");
    }
  };

  const buildVideosUrl = useCallback(
    ({ pageIndex }) => {
      const offset = pageIndex * PAGE_SIZE;

      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));

      if (filter !== "all") params.set("video_type", filter);
      if (debouncedSearch) params.set("search", debouncedSearch);

      if (sortBy === "oldest") params.set("sort_by", "oldest");
      else if (sortBy === "title_asc") params.set("sort_by", "title_asc");
      else if (sortBy === "title_desc") params.set("sort_by", "title_desc");
      else params.set("sort_by", "newest");

      return `${API}/videos?${params.toString()}`;
    },
    [filter, debouncedSearch, sortBy]
  );

  const fetchFirstPage = useCallback(async () => {
    try {
      setLoading(true);
      setHasMore(true);
      setPage(0);

      if (!BACKEND_URL) {
        console.error("REACT_APP_BACKEND_URL no está definido.");
        setVideos([]);
        setHasMore(false);
        return;
      }

      const token = await getSessionTokenOrRedirect();
      if (!token) return;

      const url = buildVideosUrl({ pageIndex: 0 });

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const list = normalizeVideos(response.data);
      setVideos(list);
      setHasMore(list.length === PAGE_SIZE);

      // ✅ refrescar cuota también (por si cambió al subir/borrar)
      fetchQuota();
    } catch (error) {
      console.error("Error fetching videos:", error);
      setVideos([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [buildVideosUrl, getSessionTokenOrRedirect, fetchQuota]);

  const fetchMore = useCallback(async () => {
    if (loadingMore || loading || !hasMore) return;

    try {
      setLoadingMore(true);

      const token = await getSessionTokenOrRedirect();
      if (!token) return;

      const nextPage = page + 1;
      const url = buildVideosUrl({ pageIndex: nextPage });

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const list = normalizeVideos(response.data);

      setVideos((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        const existing = new Set(base.map((v) => v.id));
        return [...base, ...list.filter((v) => !existing.has(v.id))];
      });

      setPage(nextPage);
      setHasMore(list.length === PAGE_SIZE);
    } catch (error) {
      console.error("Error fetching more videos:", error);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [buildVideosUrl, page, hasMore, loadingMore, loading, getSessionTokenOrRedirect]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      nav("/login");
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar este video?")) return;

    try {
      const token = await getSessionTokenOrRedirect();
      if (!token) return;

      await axios.delete(`${API}/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setVideos((prev) => (Array.isArray(prev) ? prev.filter((v) => v.id !== videoId) : []));

      // ✅ refrescar cuota tras borrar
      fetchQuota();
    } catch (error) {
      console.error("Error deleting video:", error);
      alert("Error al eliminar el video");
    }
  };

  const startEditing = (video) => {
    setEditingVideo(video.id);
    setEditForm({
      title: video.title,
      description: video.description || "",
      video_type: video.video_type,
    });
  };

  const cancelEditing = () => {
    setEditingVideo(null);
    setEditForm({ title: "", description: "", video_type: "" });
  };

  const handleEditSubmit = async (videoId) => {
    try {
      const token = await getSessionTokenOrRedirect();
      if (!token) return;

      const formData = new FormData();
      formData.append("title", editForm.title);
      formData.append("description", editForm.description);
      formData.append("video_type", editForm.video_type);

      const response = await axios.put(`${API}/videos/${videoId}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      setVideos((prev) =>
        Array.isArray(prev) ? prev.map((v) => (v.id === videoId ? response.data : v)) : []
      );
      setEditingVideo(null);
    } catch (error) {
      console.error("Error updating video:", error);
      alert("Error al actualizar el video");
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getVideoColor = (type) => {
    return type === "dance" ? "from-pink-500 to-purple-500" : "from-blue-500 to-cyan-500";
  };

  // Usuario + auth watcher
  useEffect(() => {
    loadUserLabel();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      loadUserLabel();
      fetchQuota();
    });

    return () => subscription?.unsubscribe();
  }, [loadUserLabel, fetchQuota]);

  // Cargar vídeos cuando cambian filtros/búsqueda/orden
  useEffect(() => {
    fetchFirstPage();
  }, [fetchFirstPage]);

  // ✅ Barra de cuota
  const quotaMB = bytesToMB(quota.quota_bytes);
  const usedMB = bytesToMB(quota.used_bytes);
  const remainingMB = bytesToMB(quota.remaining_bytes);
  const percent = quota.quota_bytes > 0 ? Math.min(100, Math.round((quota.used_bytes / quota.quota_bytes) * 100)) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="container mx-auto px-4 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-2xl md:text-3xl font-bold text-white">
                Arti<span className="text-yellow-400">Book</span>
              </Link>

              {/* Saludo desktop */}
              <div className="hidden md:flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-3 py-2">
                <UserIcon className="w-4 h-4 text-yellow-300" />
                <span className="text-white font-semibold">
                  {userLabel ? `Hola, ${userLabel}` : userEmail ? `Hola, ${userEmail}` : "Hola"}
                </span>

                {!hasDisplayName && (
                  <button
                    onClick={setOrEditMyName}
                    className="ml-2 text-yellow-300 underline text-sm hover:text-yellow-200"
                    title="Cambiar nombre/nickname"
                  >
                    Completar
                  </button>
                )}
              </div>
            </div>

            <div className="flex gap-2 md:gap-4">
              <Link
                to="/record"
                className="bg-yellow-400 text-purple-900 px-3 md:px-6 py-2 rounded-lg font-semibold hover:bg-yellow-300 transition flex items-center gap-1 md:gap-2 text-xs md:text-base"
              >
                <Camera className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden sm:inline">Grabar Video</span>
                <span className="sm:hidden">Grabar</span>
              </Link>

              <Link
                to="/upload"
                className="bg-white text-purple-900 px-3 md:px-6 py-2 rounded-lg font-semibold hover:bg-purple-50 transition flex items-center gap-1 md:gap-2 text-xs md:text-base"
              >
                <Upload className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden sm:inline">Subir Video</span>
                <span className="sm:hidden">Subir</span>
              </Link>

              <button
                onClick={handleLogout}
                className="bg-white/20 text-white px-3 md:px-6 py-2 rounded-lg font-semibold hover:bg-white/30 transition text-xs md:text-base"
              >
                Salir
              </button>
            </div>
          </div>

          {/* Saludo móvil */}
          <div className="md:hidden mt-3 flex items-center justify-between bg-white/10 border border-white/20 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <UserIcon className="w-4 h-4 text-yellow-300" />
              <span className="text-white font-semibold">
                {userLabel ? `Hola, ${userLabel}` : userEmail ? `Hola, ${userEmail}` : "Hola"}
              </span>
            </div>

            {!hasDisplayName && (
              <button
                onClick={setOrEditMyName}
                className="text-yellow-300 underline text-sm hover:text-yellow-200"
              >
                Completar
              </button>
            )}
          </div>

          {/* ✅ Barra de almacenamiento */}
          <div className="mt-4 bg-white/10 border border-white/20 rounded-xl p-3 md:p-4">
            <div className="flex items-center justify-between text-white text-sm md:text-base mb-2">
              <div>
                <span className="opacity-90">Almacenamiento:</span>{" "}
                <span className="font-semibold">
                  {formatMB(usedMB)} MB / {formatMB(quotaMB)} MB
                </span>{" "}
                <span className="opacity-80">({percent}%)</span>
              </div>
              <div className="opacity-90">
                Disponible: <span className="font-semibold">{formatMB(remainingMB)} MB</span>
              </div>
            </div>

            <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-3 rounded-full bg-yellow-400"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Mi Biblioteca</h1>

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-300 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar videos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 md:py-3 rounded-lg bg-white/10 backdrop-blur-lg text-white placeholder-purple-300 border border-white/20 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400 focus:outline-none text-sm md:text-base"
              />
            </div>
          </div>

          {/* Filters + Sort */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex gap-2 md:gap-4 flex-wrap">
              <button
                onClick={() => setFilter("all")}
                className={`px-4 md:px-6 py-2 rounded-lg font-semibold transition text-sm md:text-base ${
                  filter === "all" ? "bg-white text-purple-900" : "bg-white/20 text-white hover:bg-white/30"
                }`}
              >
                Todos
              </button>

              <button
                onClick={() => setFilter("dance")}
                className={`px-4 md:px-6 py-2 rounded-lg font-semibold transition flex items-center gap-2 text-sm md:text-base ${
                  filter === "dance" ? "bg-white text-purple-900" : "bg-white/20 text-white hover:bg-white/30"
                }`}
              >
                <VideoIcon className="w-4 h-4 md:w-5 md:h-5" />
                Danza
              </button>

              <button
                onClick={() => setFilter("music")}
                className={`px-4 md:px-6 py-2 rounded-lg font-semibold transition flex items-center gap-2 text-sm md:text-base ${
                  filter === "music" ? "bg-white text-purple-900" : "bg-white/20 text-white hover:bg-white/30"
                }`}
              >
                <Music className="w-4 h-4 md:w-5 md:h-5" />
                Música
              </button>
            </div>

            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none px-4 py-2 pr-10 rounded-lg bg-white/10 backdrop-blur-lg text-white border border-white/20 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400 focus:outline-none text-sm md:text-base"
              >
                <option value="newest">Más recientes</option>
                <option value="oldest">Más antiguos</option>
                <option value="title_asc">Título A-Z</option>
                <option value="title_desc">Título Z-A</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/70 w-4 h-4" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-white text-lg md:text-xl">Cargando videos...</div>
        ) : safeVideos.length === 0 ? (
          <div className="text-center text-white">
            <VideoIcon className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-4 opacity-50" />
            <h2 className="text-xl md:text-2xl font-bold mb-2">
              {debouncedSearch ? "No se encontraron videos" : "No hay videos aún"}
            </h2>
            <p className="text-purple-200 mb-6 text-sm md:text-base">
              {debouncedSearch ? "Intenta con otros términos de búsqueda" : "Comienza grabando o subiendo tu primer video"}
            </p>
          </div>
        ) : (
          <>
            {/* Grid compacto */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
              {safeVideos.map((video) => (
                <div
                  key={video.id}
                  className="bg-white/10 backdrop-blur-lg rounded-xl overflow-hidden hover:bg-white/20 transition"
                >
                  {editingVideo === video.id ? (
                    <div className="p-3 space-y-2">
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg text-sm"
                        placeholder="Título"
                      />
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg text-sm"
                        placeholder="Descripción"
                        rows="2"
                      />
                      <select
                        value={editForm.video_type}
                        onChange={(e) => setEditForm({ ...editForm, video_type: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg text-sm"
                      >
                        <option value="dance">Danza</option>
                        <option value="music">Música</option>
                      </select>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditSubmit(video.id)}
                          className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-1"
                        >
                          <Check className="w-4 h-4" />
                          Guardar
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Link to={`/video/${video.id}`}>
                        <div
                          className={`aspect-[16/10] bg-gradient-to-br ${getVideoColor(video.video_type)} flex items-center justify-center relative`}
                        >
                          {video.video_type === "dance" ? (
                            <VideoIcon className="w-9 h-9 md:w-11 md:h-11 text-white/85" />
                          ) : (
                            <Music className="w-9 h-9 md:w-11 md:h-11 text-white/85" />
                          )}

                          {video.duration && (
                            <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-white text-[10px] font-semibold">
                              {formatDuration(video.duration)}
                            </div>
                          )}
                        </div>
                      </Link>

                      <div className="p-2.5 md:p-3">
                        <Link to={`/video/${video.id}`}>
                          <h3 className="text-white font-bold text-sm md:text-base mb-1 hover:text-yellow-400 transition line-clamp-1">
                            {video.title}
                          </h3>
                        </Link>

                        <div className="flex items-center justify-between">
                          <span className="text-purple-300 text-[11px]">
                            {video.created_at ? new Date(video.created_at).toLocaleDateString() : ""}
                          </span>

                          <div className="flex gap-2">
                            <button
                              onClick={() => startEditing(video)}
                              className="text-blue-400 hover:text-blue-300 transition"
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(video.id)}
                              className="text-red-400 hover:text-red-300 transition"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Cargar más */}
            <div className="flex justify-center mt-6">
              {hasMore ? (
                <button
                  onClick={fetchMore}
                  disabled={loadingMore}
                  className="bg-white/15 text-white px-6 py-2 rounded-lg font-semibold hover:bg-white/25 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMore ? "Cargando..." : "Cargar más"}
                </button>
              ) : (
                <div className="text-white/70 text-sm mt-2">No hay más vídeos.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
